import { supabase } from '../supabaseClient'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const ASAAS_URL = process.env.REACT_APP_ASAAS_URL || 'https://sandbox.asaas.com/api/v3'

/**
 * Serviço para integração com Conta ClickPag (Subconta Asaas)
 * Consulta saldo, faz saques/transferências via Edge Functions
 */
class AsaasSubcontaService {
  /**
   * Busca configuração da Conta ClickPag do usuário
   */
  async getSubcontaConfig(userId) {
    try {
      const { data: usuario } = await supabase
        .from('usuarios')
        .select('asaas_api_key, asaas_account_id, asaas_wallet_id, modo_integracao')
        .eq('id', userId)
        .single()

      if (!usuario?.asaas_api_key || !usuario?.asaas_account_id) {
        return null
      }
      
      // Também verificar na tabela workspaces
      const { data: workspace } = await supabase
        .from('workspaces')
        .select('asaas_api_key, asaas_account_id, asaas_wallet_id')
        .eq('owner_id', userId)
        .single()
      
      if (workspace?.asaas_api_key && workspace?.asaas_account_id) {
        return {
          apiKey: workspace.asaas_api_key,
          accountId: workspace.asaas_account_id,
          walletId: workspace.asaas_wallet_id,
          ambiente: 'production'
        }
      }

      return {
        apiKey: usuario.asaas_api_key,
        accountId: usuario.asaas_account_id,
        walletId: usuario.asaas_wallet_id,
        ambiente: usuario.modo_integracao === 'asaas' ? 'production' : 'sandbox'
      }
    } catch (error) {
      console.error('Erro ao buscar config da Conta ClickPag:', error)
      return null
    }
  }

  /**
   * Consulta saldo real da Conta ClickPag via Edge Function
   * Retorna: saldo disponível, saldo a receber, saldo bloqueado
   */
  async consultarSaldo(userId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        return { 
          success: false, 
          error: 'Sessão não encontrada',
          saldo: { disponivel: 0, a_receber: 0, bloqueado: 0 }
        }
      }

      // Chamar Edge Function asaas-proxy
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/asaas-proxy?endpoint=/wallet`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const result = await response.json()
      
      if (result.success && result.data) {
        return {
          success: true,
          saldo: {
            disponivel: parseFloat(result.data.balance) || 0,
            a_receber: parseFloat(result.data.pendingBalance) || 0,
            bloqueado: parseFloat(result.data.blockedBalance) || 0,
            total: parseFloat(result.data.totalBalance) || 0
          }
        }
      }
      
      return result

    } catch (error) {
      console.error('Erro ao consultar saldo ClickPag:', error)
      return { 
        success: false, 
        error: error.message,
        saldo: { disponivel: 0, a_receber: 0, bloqueado: 0, total: 0 }
      }
    }
  }

  /**
   * Consulta estatísticas de cobranças
   */
  async consultarEstatisticas(userId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return { success: false, error: 'Sessão não encontrada' }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/asaas-proxy?endpoint=/statistics`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      )
      return await response.json()
    } catch (error) {
      console.error('Erro ao consultar estatísticas:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Consulta extrato da conta
   */
  async consultarExtrato(userId, params = {}) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return { success: false, error: 'Sessão não encontrada' }

      const queryParams = new URLSearchParams(params).toString()
      const endpoint = `/financialTransactions${queryParams ? '?' + queryParams : ''}`
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/asaas-proxy?endpoint=${encodeURIComponent(endpoint)}`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      )
      return await response.json()
    } catch (error) {
      console.error('Erro ao consultar extrato:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Solicita transferência PIX para a chave cadastrada
   * @param {string} userId 
   * @param {number} valor 
   * @param {string} chavePix - Chave PIX de destino
   * @param {string} tipoChavePix - Tipo da chave PIX (CPF, CNPJ, EMAIL, PHONE, EVP)
   * @param {string} descricao 
   */
  async solicitarSaque(userId, valor, chavePix, tipoChavePix = 'CPF', descricao = 'Saque via ClickPag') {
    try {
      const config = await this.getSubcontaConfig(userId)
      if (!config) {
        throw new Error('Conta ClickPag não criada')
      }

      if (!chavePix) {
        throw new Error('Chave PIX não informada')
      }
      // Criar transferência na Asaas
      // Usar a API de transfers para enviar para chave PIX externa
      const response = await fetch(
        `${ASAAS_URL}/transfers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'access_token': config.apiKey
          },
          body: JSON.stringify({
            value: valor,
            pixAddressKey: chavePix,
            pixAddressKeyType: tipoChavePix,
            description: descricao
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.description || 'Erro ao criar transferência')
      }

      // Registrar saque no banco
      await supabase.from('saques_clientes').insert({
        user_id: userId,
        valor: valor,
        status: data.status === 'PENDING' ? 'pendente' : 'processado',
        asaas_transfer_id: data.id,
        pix_chave: chavePix,
        pix_tipo: tipoChavePix,
        descricao: descricao
      })

      return {
        success: true,
        transferId: data.id,
        status: data.status,
        message: 'Saque solicitado com sucesso!'
      }
    } catch (error) {
      console.error('Erro ao solicitar saque:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Busca histórico de saques do cliente
   */
  async getHistoricoSaques(userId) {
    try {
      const { data, error } = await supabase
        .from('saques_clientes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Erro ao buscar histórico:', error)
      return []
    }
  }

  /**
   * Cria Conta ClickPag para o usuário (via Edge Function)
   * @deprecated Use a Edge Function criar-conta-clickpag diretamente
   */
  async criarSubconta(userId, dados) {
    try {
      // Buscar API key master do sistema (do admin/clickpag)
      const { data: config } = await supabase
        .from('config')
        .select('valor')
        .eq('chave', 'asaas_master_api_key')
        .single()

      const masterApiKey = config?.valor
      if (!masterApiKey) {
        throw new Error('Chave master do Asaas não configurada no sistema')
      }

      const response = await fetch(
        `https://api.asaas.com/v3/accounts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'access_token': masterApiKey
          },
          body: JSON.stringify({
            name: dados.nome,
            email: dados.email,
            cpfCnpj: dados.cpfCnpj,
            phone: dados.telefone,
            mobilePhone: dados.celular,
            address: dados.endereco,
            addressNumber: dados.numero,
            province: dados.bairro,
            postalCode: dados.cep,
            city: dados.cidade,
            state: dados.estado,
            country: 'BR'
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.description || 'Erro ao criar subconta')
      }

      // Salvar dados da subconta no usuário
      await supabase.from('usuarios').update({
        asaas_account_id: data.id,
        asaas_api_key: data.apiKey,
        asaas_wallet_id: data.walletId,
        modo_integracao: 'asaas'
      }).eq('id', userId)

      return {
        success: true,
        accountId: data.id,
        apiKey: data.apiKey
      }
    } catch (error) {
      console.error('Erro ao criar subconta:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
}

export const asaasSubcontaService = new AsaasSubcontaService()
export default asaasSubcontaService
